import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { PaziaFormButton, PaziaInput, PaziaCheckbox, PaziaTextarea, PaziaFileDropzone, PaziaSelect } from "components/atoms";
import { LandingPageApi } from "api/shop";
import { useCategoriesOptions } from "api/categories/useCategoriesOptions";
import { useShopImageUpload } from "pages/Shop/Sections/SectionForms/useShopImageUpload";

interface LandingPageFormProps {
  onSubmit: (data: LandingPageFormValues) => void;
  landingPage?: LandingPageApi;
  isLoading?: boolean;
}

export const LandingPageForm = ({ onSubmit, landingPage, isLoading }: LandingPageFormProps) => {
  const { uploadImage } = useShopImageUpload();
  const { options: categoryOptions, loading: categoriesLoading } = useCategoriesOptions();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      [FORM_NAMES.NAME]: landingPage?.name ?? "",
      [FORM_NAMES.CATEGORY_ID]: landingPage?.categoryId ?? null,
      [FORM_NAMES.SLUG]: landingPage?.slug ?? "",
      [FORM_NAMES.IS_PUBLISHED]: landingPage?.isPublished ?? false,
      [FORM_NAMES.META_TITLE]: landingPage?.metaTitle ?? "",
      [FORM_NAMES.META_DESCRIPTION]: landingPage?.metaDescription ?? "",
      [FORM_NAMES.OG_IMAGE]: landingPage?.ogImage ?? "",
      [FORM_NAMES.OG_IMAGE_ALT]: landingPage?.ogImageAlt ?? "",
    },
  });
  const { handleSubmit, watch, setValue } = methods;

  // Auto-generate slug from name if slug is empty
  const name = watch(FORM_NAMES.NAME);
  const currentSlug = watch(FORM_NAMES.SLUG);
  const categoryId = watch(FORM_NAMES.CATEGORY_ID);
  const isCategoryLinked = !!categoryId;

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[ąàáâãäå]/g, "a")
      .replace(/[ćčç]/g, "c")
      .replace(/[ęèéêë]/g, "e")
      .replace(/[ìíîï]/g, "i")
      .replace(/[łľ]/g, "l")
      .replace(/[ńñň]/g, "n")
      .replace(/[óòôõö]/g, "o")
      .replace(/[śšş]/g, "s")
      .replace(/[ùúûü]/g, "u")
      .replace(/[ýÿ]/g, "y")
      .replace(/[żźž]/g, "z")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleNameBlur = () => {
    if (!currentSlug && name) {
      setValue(FORM_NAMES.SLUG, generateSlug(name));
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-xl gap-4">
        <PaziaInput name={FORM_NAMES.NAME} label="Nazwa strony" required onBlur={handleNameBlur} />
        <PaziaSelect
          name={FORM_NAMES.CATEGORY_ID}
          label="Kategoria (opcjonalne)"
          options={[{ value: "", label: "Brak — strona niezwiązana z kategorią" }, ...categoryOptions]}
          disabled={categoriesLoading}
        />
        {isCategoryLinked && (
          <p className="-mt-2 text-xs text-amber-600 dark:text-amber-400">
            Slug jest generowany automatycznie z kategorii i nie może być edytowany.
          </p>
        )}
        <PaziaInput
          name={FORM_NAMES.SLUG}
          label="Slug (URL)"
          required={!isCategoryLinked}
          disabled={isCategoryLinked}
          placeholder={isCategoryLinked ? "Generowany z kategorii" : "np. /, home, akademia/statut"}
        />
        <p className="-mt-2 text-xs text-slate-500">
          Użyj "/" dla strony głównej. Obsługiwane są zagnieżdżone ścieżki, np. akademia/statut
        </p>
        <PaziaCheckbox name={FORM_NAMES.IS_PUBLISHED} label="Opublikowana" />
        <p className="mt-2 text-xs font-semibold text-slate-600">SEO</p>
        <PaziaInput name={FORM_NAMES.META_TITLE} label="Meta title (SEO)" />
        <PaziaTextarea name={FORM_NAMES.META_DESCRIPTION} label="Meta description (SEO)" />
        <p className="mt-2 text-xs font-semibold text-slate-600">OG Image (Social Media)</p>
        <div className="[&_img]:max-h-48">
          <PaziaFileDropzone
            name={FORM_NAMES.OG_IMAGE}
            label="OG Image"
            type="image"
            single
            preview="image"
            onDrop={async (file: File) => {
              const url = await uploadImage(file as File);
              setValue(FORM_NAMES.OG_IMAGE, url);
            }}
          />
        </div>
        <PaziaInput name={FORM_NAMES.OG_IMAGE_ALT} label="OG Image Alt (opis obrazka)" />
        <PaziaFormButton disabled={isLoading} />
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  NAME: "name",
  CATEGORY_ID: "categoryId",
  SLUG: "slug",
  IS_PUBLISHED: "isPublished",
  META_TITLE: "metaTitle",
  META_DESCRIPTION: "metaDescription",
  OG_IMAGE: "ogImage",
  OG_IMAGE_ALT: "ogImageAlt",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.NAME]: yup.string().required("Nazwa jest wymagana"),
    [FORM_NAMES.CATEGORY_ID]: yup.number().nullable().optional().transform((value) => (value === "" || value === undefined ? null : value)),
    [FORM_NAMES.SLUG]: yup
      .string()
      .when(FORM_NAMES.CATEGORY_ID, {
        is: (val: any) => !val,
        then: (schema) => schema
          .required("Slug jest wymagany")
          .matches(
            /^(\/|[a-z0-9-]+(?:\/[a-z0-9-]+)*)$/,
            "Slug może zawierać '/' dla strony głównej lub ścieżkę np. akademia/statut (małe litery, cyfry, myślniki)",
          ),
        otherwise: (schema) => schema.optional(),
      }),
    [FORM_NAMES.IS_PUBLISHED]: yup.boolean().default(false),
    [FORM_NAMES.META_TITLE]: yup.string().optional(),
    [FORM_NAMES.META_DESCRIPTION]: yup.string().optional(),
    [FORM_NAMES.OG_IMAGE]: yup.string().optional(),
    [FORM_NAMES.OG_IMAGE_ALT]: yup.string().optional(),
  })
  .required();

export type LandingPageFormValues = yup.InferType<typeof schema>;
